<template>
    <div class="task-module-rules">
        <div class="page-header">
            <div class="header-left">
                <h2>{{ $t("taskModuleRules.title") }}</h2>
                <p class="description">
                    {{ $t("taskModuleRules.description") }}
                </p>
            </div>
            <div class="header-right">
                <a-switch :checked="enabled" @change="onMasterToggle" size="large" />
                <span class="toggle-label">{{ enabled ? $t("taskModuleRules.active") : $t("taskModuleRules.passive") }}</span>
            </div>
        </div>

        <div v-if="loading" class="loading-container">
            <a-spin size="large" />
        </div>

        <div v-else-if="enabled" class="rules-container">
            <!-- Rules List -->
            <draggable v-model="rules" handle=".drag-handle" @end="onRulesReordered" class="rules-list">
                <div v-for="rule in rules" :key="rule.id" class="rule-card" :class="{ disabled: !rule.enabled }">
                    <div class="rule-header">
                        <div class="rule-header-left">
                            <i class="material-icons drag-handle">drag_indicator</i>
                            <span class="rule-name">{{ rule.name }}</span>
                            <div class="rule-badges">
                                <a-tooltip v-if="rule.triggerOtherRules" :title="$t('taskModuleRules.chainBadgeTooltip')">
                                    <span class="rule-badge chain-badge">
                                        <i class="material-icons">link</i>
                                        {{ $t("taskModuleRules.chainBadge") }}
                                    </span>
                                </a-tooltip>
                                <a-tooltip v-if="rule.reportToAutomation === false" :title="$t('taskModuleRules.automationOffTooltip')">
                                    <span class="rule-badge no-automation-badge">
                                        <i class="material-icons">notifications_off</i>
                                        {{ $t("taskModuleRules.automationOffBadge") }}
                                    </span>
                                </a-tooltip>
                            </div>
                        </div>
                        <div class="rule-header-right">
                            <a-switch v-model="rule.enabled" size="small" @change="onRuleToggle(rule.id, $event)" />
                            <a-button type="text" size="small" class="icon-button" @click="editRule(rule)">
                                <i class="material-icons">edit</i>
                            </a-button>
                            <a-button type="text" size="small" danger class="icon-button" @click="confirmDeleteRule(rule)">
                                <i class="material-icons">delete</i>
                            </a-button>
                        </div>
                    </div>

                    <div class="rule-body">
                        <div class="rule-summary">
                            <div class="summary-line when-summary">
                                <i class="material-icons when-icon">{{ getWhenIcon(rule.when) }}</i>
                                <strong>{{ $t("taskModuleRules.when") }}</strong>
                                <span>{{ getWhenLabel(rule.when) }}</span>
                            </div>

                            <div class="summary-line conditions-summary">
                                <i class="material-icons condition-icon">filter_list</i>
                                <strong>{{ $t("taskModuleRules.conditions") }}</strong>
                                <span v-html="getConditionsSummary(rule.conditions)"></span>
                            </div>

                            <div class="summary-line actions-summary">
                                <i class="material-icons action-icon">bolt</i>
                                <strong>{{ $t("taskModuleRules.actions") }}</strong>
                                <span v-html="getActionsSummary(rule.actions)"></span>
                            </div>
                        </div>
                    </div>
                </div>
            </draggable>

            <!-- Else Fallback Card -->
            <div class="else-card">
                <div class="else-header">
                    <div class="else-title">
                        <i class="material-icons">block</i>
                        <span>{{ $t("taskModuleRules.elseTitle") }}</span>
                    </div>
                    <a-button type="text" size="small" class="icon-button" @click="editElseActions">
                        <i class="material-icons">edit</i>
                    </a-button>
                </div>
                <div class="else-body">
                    <div v-if="!elseActions || elseActions.length === 0" class="no-actions">
                        {{ $t("taskModuleRules.noActionsDefined") }}
                    </div>
                    <div v-else class="else-summary">
                        <div class="summary-line actions-summary">
                            <i class="material-icons">bolt</i>
                            <strong>{{ $t("taskModuleRules.actions") }}</strong>
                            <span v-html="getActionsSummary(elseActions)"></span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Add Rule Button -->
            <div class="add-rule-btn" @click="createNewRule">
                <i class="material-icons">add_circle</i>
                <span>{{ $t("taskModuleRules.addNewRule") }}</span>
            </div>
        </div>

        <div v-else class="empty-state">
            <div class="empty-icon">
                <i class="material-icons">account_tree</i>
            </div>
            <h3>{{ $t("taskModuleRules.rulesDisabled") }}</h3>
            <p>{{ $t("taskModuleRules.enableHint") }}</p>
        </div>

        <!-- Rule Editor Modal -->
        <RuleEditor
            v-if="showRuleEditor"
            :rule="editingRule"
            :workflow-id="workflowId"
            :visible="showRuleEditor"
            @close="closeRuleEditor"
            @save="saveRule"
        />

        <!-- Else Actions Editor Modal -->
        <ElseActionsEditor
            v-if="showElseActionsEditor"
            :actions="elseActions"
            :workflow-id="workflowId"
            :visible="showElseActionsEditor"
            @close="closeElseActionsEditor"
            @save="saveElseActions"
        />
    </div>
</template>

<script>
import { mapState } from "vuex";
import draggable from "vuedraggable";
import RuleEditor from "./RuleEditor.vue";
import ElseActionsEditor from "./components/ElseActionsEditor.vue";
import log2 from "../../../../../utils/log2";
import _ from "lodash";

const log = log2("TaskModuleRules");

export default {
    name: "TaskModuleRules",
    components: {
        draggable,
        RuleEditor,
        ElseActionsEditor,
    },
    props: {
        workflowId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            showRuleEditor: false,
            showElseActionsEditor: false,
            editingRule: null,
            loading: false,
        };
    },
    computed: {
        ...mapState({
            workflows: (state) => state.tasks.workflows,
        }),
        currentWorkflow() {
            return this.workflows.find((w) => w._id === this.workflowId);
        },
        taskModuleRules() {
            if (!this.currentWorkflow || !this.currentWorkflow.taskModuleRules) {
                return { enabled: false, rules: [], elseActions: [] };
            }
            return this.currentWorkflow.taskModuleRules;
        },
        rules: {
            get() {
                return this.taskModuleRules.rules || [];
            },
            set(value) {
                this.updateWorkflowRules({ rules: value });
            },
        },
        elseActions: {
            get() {
                return this.taskModuleRules.elseActions || [];
            },
            set(value) {
                this.updateWorkflowRules({ elseActions: value });
            },
        },
        enabled: {
            get() {
                return this.taskModuleRules.enabled || false;
            },
        },
    },
    methods: {
        async updateWorkflowRules(updates) {
            const taskModuleRules = _.cloneDeep(this.taskModuleRules);
            Object.assign(taskModuleRules, updates);

            await this.$store.dispatch("tasks/updateWorkflow", {
                workflowId: this.workflowId,
                params: { taskModuleRules },
            });
        },

        async onMasterToggle(enabled) {
            this.loading = true;
            try {
                await this.updateWorkflowRules({ enabled });
                this.$message.success(
                    enabled ? this.$t("taskModuleRules.rulesEnabledSuccess") : this.$t("taskModuleRules.rulesDisabledSuccess")
                );
            } catch (error) {
                log("onMasterToggle error", error);
                this.$message.error(this.$t("taskModuleRules.ruleUpdateError"));
            } finally {
                this.loading = false;
            }
        },

        async onRuleToggle(ruleId, enabled) {
            try {
                const rules = _.cloneDeep(this.rules);
                const rule = rules.find((r) => r.id === ruleId);
                if (rule) {
                    rule.enabled = enabled;
                    rule.updatedAt = new Date().toISOString();
                    await this.updateWorkflowRules({ rules });
                    this.$message.success(enabled ? this.$t("taskModuleRules.ruleEnabledSuccess") : this.$t("taskModuleRules.ruleDisabledSuccess"));
                }
            } catch (error) {
                log("onRuleToggle error", error);
                this.$message.error(this.$t("taskModuleRules.ruleUpdateError"));
            }
        },

        async onRulesReordered() {
            try {
                const rules = _.cloneDeep(this.rules);
                rules.forEach((rule, index) => {
                    rule.order = index;
                });
                await this.updateWorkflowRules({ rules });
                this.$message.success(this.$t("taskModuleRules.orderUpdatedSuccess"));
            } catch (error) {
                log("onRulesReordered error", error);
                this.$message.error(this.$t("taskModuleRules.orderUpdateError"));
            }
        },

        createNewRule() {
            this.editingRule = {
                id: this.generateUUID(),
                name: this.$t("taskModuleRules.ruleNameTemplate", { n: this.rules.length + 1 }),
                enabled: true,
                order: this.rules.length,
                when: "task_created",
                conditions: {
                    filterGroups: [],
                    groupOperator: "AND",
                },
                actions: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            this.showRuleEditor = true;
        },

        editRule(rule) {
            this.editingRule = _.cloneDeep(rule);
            this.showRuleEditor = true;
        },

        closeRuleEditor() {
            this.showRuleEditor = false;
            this.editingRule = null;
        },

        async saveRule(rule) {
            try {
                const rules = _.cloneDeep(this.rules);
                const existingIndex = rules.findIndex((r) => r.id === rule.id);

                if (existingIndex !== -1) {
                    rules[existingIndex] = rule;
                } else {
                    rules.push(rule);
                }

                await this.updateWorkflowRules({ rules });
                this.$message.success(this.$t("taskModuleRules.ruleSavedSuccess"));
                this.closeRuleEditor();
            } catch (error) {
                log("saveRule error", error);
                this.$message.error(this.$t("taskModuleRules.ruleSaveError"));
            }
        },

        confirmDeleteRule(rule) {
            this.$confirm({
                title: this.$t("taskModuleRules.deleteRuleTitle"),
                content: this.$t("taskModuleRules.deleteRuleConfirm", { name: rule.name }),
                okText: this.$t("taskModuleRules.delete"),
                okType: "danger",
                cancelText: this.$t("taskModuleRules.cancel"),
                onOk: async () => {
                    try {
                        const rules = this.rules.filter((r) => r.id !== rule.id);
                        await this.updateWorkflowRules({ rules });
                        this.$message.success(this.$t("taskModuleRules.ruleDeletedSuccess"));
                    } catch (error) {
                        log("confirmDeleteRule error", error);
                        this.$message.error(this.$t("taskModuleRules.ruleDeleteError"));
                    }
                },
            });
        },

        editElseActions() {
            this.showElseActionsEditor = true;
        },

        closeElseActionsEditor() {
            this.showElseActionsEditor = false;
        },

        async saveElseActions(actions) {
            try {
                await this.updateWorkflowRules({ elseActions: actions });
                this.$message.success(this.$t("taskModuleRules.elseActionsSavedSuccess"));
                this.closeElseActionsEditor();
            } catch (error) {
                log("saveElseActions error", error);
                this.$message.error(this.$t("taskModuleRules.actionsSaveError"));
            }
        },

        getWhenLabel(when) {
            const labels = {
                task_created: "Görev Oluşturulduğunda",
                stage_changed: "Stage Değiştiğinde",
                completed_changed: "Completed Durumu Değiştiğinde",
                priority_changed: "Priority Değiştiğinde",
                assignees_changed: "Assignees Değiştiğinde",
                owner_assigned: "Owner Atandığında",
                collaborators_changed: "Collaborators Değiştiğinde",
                followers_changed: "Followers Değiştiğinde",
                done_changed: "Done Durumu Değiştiğinde",
                archived_changed: "Arşiv Durumu Değiştiğinde",
                tags_changed: "Etiketler Değiştiğinde",
                checklist_updated: "Checklist İlerlediğinde",
                checklist_completed: "Tüm Checklist Tamamlandığında",
                comment_added: "Yorum Eklendiğinde",
            };
            return labels[when] || when;
        },

        getWhenIcon(when) {
            const icons = {
                task_created: "add_task",
                stage_changed: "arrow_forward",
                completed_changed: "check_circle",
                priority_changed: "flag",
                assignees_changed: "person_add",
                owner_assigned: "assignment_ind",
                collaborators_changed: "group_add",
                followers_changed: "visibility",
                done_changed: "done_all",
                archived_changed: "archive",
                tags_changed: "label",
                checklist_updated: "checklist",
                checklist_completed: "task_alt",
                comment_added: "comment",
            };
            return icons[when] || "schedule";
        },

        getConditionsSummary(conditions) {
            if (!conditions || !conditions.filterGroups || conditions.filterGroups.length === 0) {
                return '<span class="no-condition">' + this.$t("taskModuleRules.noCondition") + "</span>";
            }

            const groupSummaries = conditions.filterGroups.map((group) => {
                const filterSummaries = group.filters
                    .map((filter) => {
                        return this.getFilterSummary(filter);
                    })
                    .join(" <strong>" + (group.operator === "AND" ? this.$t("taskModuleRules.and") : this.$t("taskModuleRules.or")) + "</strong> ");

                return `(${filterSummaries})`;
            });

            return groupSummaries.join(
                " <strong class=\"group-op\">" + (conditions.groupOperator === "AND" ? this.$t("taskModuleRules.AND") : this.$t("taskModuleRules.OR")) + "</strong> "
            );
        },

        getFilterSummary(filter) {
            const fieldLabel = this.getFieldLabel(filter.field);
            const operatorLabel = this.getOperatorLabel(filter.operator);
            const valueLabel = this.getValueLabel(filter.field, filter.value);

            if (!filter.field) return '<span class="incomplete">' + this.$t("taskModuleRules.fieldNotSelected") + "</span>";
            if (!filter.operator) return `<span class="field-name">${fieldLabel}</span>`;
            if (!this.needsValue(filter.operator))
                return `<span class="field-name">${fieldLabel}</span> ${operatorLabel}`;

            return `<span class="field-name">${fieldLabel}</span> ${operatorLabel} <span class="value-highlight">${valueLabel}</span>`;
        },

        getFieldLabel(field) {
            const labels = {
                "task.title": "Başlık",
                "task.description": "Açıklama",
                "task.priority": "Öncelik",
                "task.pipelineStage": "Stage",
                "task.completed": "Completed",
                "task.done": "Done",
                "task.archived": "Arşivlenmiş",
                "task.owner.exists": "Sahibi",
                "task.owner": "Sahibi",
                "task.assignees": "Atananları",
                "task.followers": "Takipçileri",
                "task.collaborators": "İşbirlikçileri",
                "task.tags": "Etiketleri",
                "task.startDate": "Başlangıç Tarihi",
                "task.endDate": "Bitiş Tarihi",
                "task.hasAttachment": "Ek Dosya",
                "task.checklist.progress": "Checklist İlerleme",
                "task.estimatedTime": "Tahmini Süre",
                "task.relation.exists": "İlişki",
                "task.relation.type": "İlişki Tipi",
                "task.relation.visitor.fullname": "İlişkili Kişinin Adı",
                "task.relation.visitor.email": "İlişkili Kişinin E-postası",
                "task.relation.conversation.status": "İlişkili Konuşma Durumu",
                "system.timezone.hour": "Saat",
                "system.timezone.day": "Gün",
            };
            return labels[field] || field;
        },

        getOperatorLabel(operator) {
            const labels = {
                equals: "eşittir",
                not_equals: "eşit değildir",
                contains: "içerir",
                not_contains: "içermez",
                gt: "büyüktür",
                gte: "≥",
                lt: "küçüktür",
                lte: "≤",
                in: "içerir",
                not_in: "içermez",
                exists: "var",
                not_exists: "yok",
            };
            return labels[operator] || operator;
        },

        getValueLabel(field, value) {
            if (value === null || value === undefined || value === "") return "-";

            if (field === "task.tags" && Array.isArray(value)) {
                const tagNames = value.map((tagId) => {
                    const tag = this.$store.state.tag.tags.data.find((t) => t._id === tagId);
                    return tag ? tag.name : tagId;
                });
                return tagNames.join(", ");
            }

            if (field === "task.priority") {
                const priorities = {
                    lowest: "En Düşük",
                    low: "Düşük",
                    medium: "Orta",
                    high: "Yüksek",
                    highest: "En Yüksek"
                };
                return priorities[value] || value;
            }

            if (field === "task.pipelineStage") {
                const workflow = this.$store.state.tasks.workflows.find((w) => w._id === this.workflowId);
                const stage = (workflow.pipeline||[]).find((s) => s._id === value);
                return stage ? stage.name : value;
            }

            if (field === "task.owner" || field === "task.assignees" || field === "task.followers" || field === "task.collaborators") {
                if (Array.isArray(value)) {
                    const userNames = value.map((userId) => {
                        const user = this.$store.state.user.users.data.find((u) => u._id === userId);
                        return user ? user.name : userId;
                    });
                    return userNames.join(", ");
                }
                const user = this.$store.state.user.users.data.find((u) => u._id === value);
                return user ? user.name : value;
            }

            if (typeof value === "boolean") {
                return value ? this.$t("taskModuleRules.yes") : this.$t("taskModuleRules.no");
            }

            return value.toString();
        },

        getActionsSummary(actions) {
            if (!actions || actions.length === 0) {
                return '<span class="no-action">' + this.$t("taskModuleRules.noAction") + "</span>";
            }

            const actionSummaries = actions
                .map((action) => {
                    return this.getActionSummary(action);
                })
                .filter((s) => s)
                .join(", ");

            return actionSummaries || '<span class="no-action">Aksiyon yok</span>';
        },

        getActionSummary(action) {
            if (!action.type) return '<span class="incomplete">Aksiyon seçilmedi</span>';

            if (action.type === 'add-assignee') {
                const config = action.addAssignee;
                if (config.method === 'static') {
                    const users = config.useAllUsers ? "tüm kullanıcılar" :
                        config.userList && config.userList.length > 0 ? `${config.userList.length} kullanıcı` : "seçilmedi";
                    return `<span class="action-assignee"><strong>Assignee ekle</strong> (${users})</span>`;
                } else if (config.method === 'algorithmic') {
                    const algo = config.algorithmSettings.algorithm === 'least_task' ? "En az görevli" : "Sıralı";
                    const dateRange = config.algorithmSettings.dateRange === "today" ? "bugün" :
                        config.algorithmSettings.dateRange === "last_7_days" ? "son 7 gün" : "son 30 gün";
                    const users = config.useAllUsers ? "tüm kullanıcılar" :
                        config.userList && config.userList.length > 0 ? `${config.userList.length} kullanıcı` : "tüm kullanıcılar";
                    return `<span class="action-assignee"><strong>Algoritmik assignee</strong> (${algo}, ${dateRange}, ${users})</span>`;
                } else if (config.method === 'random') {
                    const users = config.useAllUsers ? "tüm kullanıcılar" :
                        config.userList && config.userList.length > 0 ? `${config.userList.length} kullanıcı` : "tüm kullanıcılar";
                    return `<span class="action-assignee"><strong>Rastgele assignee</strong> (${users})</span>`;
                }
            }

            if (action.type === 'remove-assignee') {
                const userCount = action.removeAssignee && action.removeAssignee.userList ? action.removeAssignee.userList.length : 0;
                return `<span class="action-remove">Assignee çıkar (${userCount} kullanıcı)</span>`;
            }

            if (action.type === 'change-owner') {
                const userId = action.changeOwner ? action.changeOwner.userId : null;
                if (!userId) return '<span class="action-owner">Sahibi <strong>sahipsiz</strong> yap</span>';
                const user = this.$store.state.user.users.data.find((u) => u._id === userId);
                const userName = user ? user.name : this.$t("taskModuleRules.unknown");
                return `<span class="action-owner">Sahibi <strong>${userName}</strong>'a ata</span>`;
            }

            if (action.type === 'add-follower') {
                const userCount = action.addFollower && action.addFollower.userList ? action.addFollower.userList.length : 0;
                return `<span class="action-follower">Takipçi ekle (${userCount} kullanıcı)</span>`;
            }

            if (action.type === 'remove-follower') {
                const userCount = action.removeFollower && action.removeFollower.userList ? action.removeFollower.userList.length : 0;
                return `<span class="action-remove">Takipçi çıkar (${userCount} kullanıcı)</span>`;
            }

            if (action.type === 'move-stage') {
                const stageId = action.moveStage ? action.moveStage.stageId : null;
                if (!stageId) return '<span class="action-stage">Stage taşı (seçilmedi)</span>';
                const workflow = this.$store.state.tasks.workflows.find((w) => w._id === this.workflowId);
                const stage = (workflow.pipeline||[]).find((s) => s._id === stageId);
                const stageName = stage ? stage.name : stageId;
                return `<span class="action-stage">Stage'i <strong>${stageName}</strong>'a taşı</span>`;
            }

            if (action.type === 'change-priority') {
                const priority = action.changePriority ? action.changePriority.priority : null;
                const priorities = {
                    lowest: "En Düşük",
                    low: "Düşük",
                    medium: "Orta",
                    high: "Yüksek",
                    highest: "En Yüksek"
                };
                const priorityLabel = priorities[priority] || priority || "seçilmedi";
                return `<span class="action-priority">Önceliği <strong>${priorityLabel}</strong> yap</span>`;
            }

            if (action.type === 'add-collaborator') {
                const userCount = action.addCollaborator && action.addCollaborator.userList ? action.addCollaborator.userList.length : 0;
                return `<span class="action-collaborator">İşbirlikçi ekle (${userCount} kullanıcı)</span>`;
            }

            if (action.type === 'mark-completed') {
                return '<span class="action-mark"><strong>Completed</strong> olarak işaretle</span>';
            }

            if (action.type === 'mark-done') {
                return '<span class="action-mark"><strong>Done</strong> olarak işaretle</span>';
            }

            if (action.type === 'mark-archived') {
                return '<span class="action-mark"><strong>Arşivle</strong></span>';
            }

            if (action.type === 'add-tag') {
                const tagIds = action.addTag && action.addTag.tagIds ? action.addTag.tagIds : [];
                if (tagIds.length === 0) return '<span class="action-tag">Etiket ekle (seçilmedi)</span>';
                const tagNames = tagIds.map((tagId) => {
                    const tag = this.$store.state.tag.tags.data.find((t) => t._id === tagId);
                    return tag ? tag.name : tagId;
                });
                return `<span class="action-tag"><strong>${tagNames.join(", ")}</strong> ${tagIds.length > 1 ? "etiketlerini" : "etiketini"} ekle</span>`;
            }

            if (action.type === 'remove-tag') {
                const tagIds = action.removeTag && action.removeTag.tagIds ? action.removeTag.tagIds : [];
                if (tagIds.length === 0) return '<span class="action-remove">Etiket çıkar (seçilmedi)</span>';
                const tagNames = tagIds.map((tagId) => {
                    const tag = this.$store.state.tag.tags.data.find((t) => t._id === tagId);
                    return tag ? tag.name : tagId;
                });
                return `<span class="action-remove"><strong>${tagNames.join(", ")}</strong> ${tagIds.length > 1 ? "etiketlerini" : "etiketini"} çıkar</span>`;
            }

            if (action.type === 'set-start-date') {
                const config = action.setStartDate || {};
                if (config.dateType === 'static') {
                    const dateStr = config.staticDate ? this.$moment(config.staticDate).format('DD/MM/YYYY') : 'seçilmedi';
                    return `<span class="action-date">Başlangıç tarihini <strong>${dateStr}</strong> olarak belirle</span>`;
                } else if (config.dateType === 'relative') {
                    const units = { days: 'gün', hours: 'saat', minutes: 'dakika' };
                    const unitLabel = units[config.relativeUnit] || config.relativeUnit;
                    return `<span class="action-date">Başlangıç tarihini <strong>${config.relativeValue} ${unitLabel} sonra</strong> olarak belirle</span>`;
                }
            }

            if (action.type === 'set-end-date') {
                const config = action.setEndDate || {};
                if (config.dateType === 'static') {
                    const dateStr = config.staticDate ? this.$moment(config.staticDate).format('DD/MM/YYYY') : 'seçilmedi';
                    return `<span class="action-date">Bitiş tarihini <strong>${dateStr}</strong> olarak belirle</span>`;
                } else if (config.dateType === 'relative') {
                    const units = { days: 'gün', hours: 'saat', minutes: 'dakika' };
                    const unitLabel = units[config.relativeUnit] || config.relativeUnit;
                    return `<span class="action-date">Bitiş tarihini <strong>${config.relativeValue} ${unitLabel} sonra</strong> olarak belirle</span>`;
                }
            }

            if (action.type === 'create-checklist') {
                const itemCount = action.createChecklist && action.createChecklist.items ? action.createChecklist.items.length : 0;
                return `<span class="action-checklist">Checklist oluştur (${itemCount} öğe)</span>`;
            }

            if (action.type === 'create-comment') {
                const commentPreview = action.createComment && action.createComment.comment ?
                    action.createComment.comment.substring(0, 50) + (action.createComment.comment.length > 50 ? '...' : '') :
                    'boş';
                return `<span class="action-comment">Yorum ekle: <em>"${commentPreview}"</em></span>`;
            }

            if (action.type === 'send-email') {
                const config = action.sendEmail || {};
                let recipients = '';
                if (config.toType === 'static') {
                    recipients = config.toStatic && config.toStatic.length > 0 ?
                        `${config.toStatic.length} alıcı` : 'seçilmedi';
                } else if (config.toType === 'dynamic') {
                    const dynamicLabels = {
                        task_owner: 'Görev Sahibi',
                        task_assignees: 'Atananlar',
                        task_followers: 'Takipçiler',
                        task_collaborators: 'İşbirlikçiler',
                        task_relation_visitor: 'İlişkili Müşteri'
                    };
                    const dynamicRecipients = (config.toDynamic || []).map(d => dynamicLabels[d] || d);
                    recipients = dynamicRecipients.length > 0 ? dynamicRecipients.join(', ') : 'seçilmedi';
                }
                const subject = config.subject || 'konusuz';
                return `<span class="action-email">E-posta gönder: <strong>"${subject}"</strong> → ${recipients}</span>`;
            }

            if (action.type === 'send-web-notification') {
                const config = action.sendWebNotification || {};
                let users = '';
                if (config.userType === 'static') {
                    users = config.userStatic && config.userStatic.length > 0 ?
                        `${config.userStatic.length} kullanıcı` : 'seçilmedi';
                } else if (config.userType === 'dynamic') {
                    const dynamicLabels = {
                        task_owner: 'Görev Sahibi',
                        task_assignees: 'Atananlar',
                        task_followers: 'Takipçiler',
                        task_collaborators: 'İşbirlikçiler'
                    };
                    const dynamicUsers = (config.userDynamic || []).map(d => dynamicLabels[d] || d);
                    users = dynamicUsers.length > 0 ? dynamicUsers.join(', ') : 'seçilmedi';
                }
                const messagePreview = config.message ?
                    config.message.substring(0, 40) + (config.message.length > 40 ? '...' : '') :
                    'boş';
                return `<span class="action-notification">Web bildirimi gönder: <em>"${messagePreview}"</em> → ${users}</span>`;
            }

            if (action.type === 'webhook') {
                const config = action.webhook || {};
                const method = config.method || 'POST';
                const url = config.url || 'URL belirtilmedi';
                const urlPreview = url.length > 50 ? url.substring(0, 50) + '...' : url;
                return `<span class="action-webhook"><strong>${method}</strong> → <em>${urlPreview}</em></span>`;
            }

            return this.getActionLabel(action.type);
        },

        getActionLabel(type) {
            const labels = {
                'add-assignee': 'Assignee Ekle',
                'remove-assignee': 'Assignee Çıkar',
                'change-owner': 'Sahip Değiştir',
                'add-follower': 'Takipçi Ekle',
                'remove-follower': 'Takipçi Çıkar',
                'move-stage': 'Stage Taşı',
                'change-priority': 'Öncelik Değiştir',
                'add-collaborator': 'İşbirlikçi Ekle',
                'mark-completed': 'Completed İşaretle',
                'mark-done': 'Done İşaretle',
                'mark-archived': 'Arşivle',
                'add-tag': 'Etiket Ekle',
                'remove-tag': 'Etiket Çıkar',
                'set-start-date': 'Başlangıç Tarihi Belirle',
                'set-end-date': 'Bitiş Tarihi Belirle',
                'create-checklist': 'Checklist Oluştur',
                'create-comment': 'Yorum Ekle',
                'send-email': 'E-posta Gönder',
                'send-web-notification': 'Web Bildirimi Gönder',
                'webhook': 'Webhook Çağır'
            };
            return labels[type] || type;
        },

        needsValue(operator) {
            return !["exists", "not_exists"].includes(operator);
        },

        generateUUID() {
            return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
                const r = (Math.random() * 16) | 0;
                const v = c === "x" ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.task-module-rules {
    padding: 24px;
    max-width: 1400px;

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 32px;
        padding-bottom: 24px;
        border-bottom: 2px solid #f0f0f0;

        .header-left {
            h2 {
                font-size: 24px;
                font-weight: 600;
                color: #1a1a1a;
                margin: 0 0 8px 0;
            }

            .description {
                color: #666;
                font-size: 14px;
                margin: 0;
            }
        }

        .header-right {
            display: flex;
            align-items: center;
            gap: 12px;

            .toggle-label {
                font-weight: 600;
                font-size: 16px;
            }
        }
    }

    .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
    }

    .rules-container {
        .rules-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-bottom: 16px;
        }

        .rule-card {
            background: #ffffff;
            border: 2px solid #e8e8e8;
            border-radius: 12px;
            padding: 20px;
            transition: all 0.3s ease;

            &:hover {
                border-color: #1890ff;
                box-shadow: 0 4px 12px rgba(24, 144, 255, 0.1);
            }

            &.disabled {
                opacity: 0.6;
                background: #fafafa;
            }

            .rule-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 16px;

                .rule-header-left {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex: 1;

                    .drag-handle {
                        cursor: grab;
                        color: #999;
                        font-size: 20px;

                        &:active {
                            cursor: grabbing;
                        }
                    }

                    .rule-name {
                        font-size: 16px;
                        font-weight: 600;
                        color: #1a1a1a;
                    }

                    .rule-badges {
                        display: flex;
                        gap: 8px;
                        align-items: center;

                        .rule-badge {
                            display: inline-flex;
                            align-items: center;
                            gap: 4px;
                            padding: 4px 10px;
                            border-radius: 12px;
                            font-size: 11px;
                            font-weight: 600;
                            white-space: nowrap;

                            .material-icons {
                                font-size: 14px;
                            }

                            &.chain-badge {
                                background: #e6f7ff;
                                color: #1890ff;
                                border: 1px solid #91d5ff;
                            }

                            &.no-automation-badge {
                                background: #fff7e6;
                                color: #fa8c16;
                                border: 1px solid #ffd591;
                            }
                        }
                    }
                }

                .rule-header-right {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .icon-button {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 6px;
                        width: 32px;
                        height: 32px;
                        border-radius: 6px;
                        transition: all 0.2s ease;

                        .material-icons {
                            font-size: 18px;
                            line-height: 1;
                        }

                        &:hover {
                            background-color: rgba(0, 0, 0, 0.04);
                        }

                        &.ant-btn-dangerous:hover {
                            background-color: rgba(255, 77, 79, 0.1);

                            .material-icons {
                                color: #ff4d4f;
                            }
                        }
                    }
                }
            }

            .rule-body {
                .rule-summary {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;

                    .summary-line {
                        display: flex;
                        align-items: flex-start;
                        gap: 8px;
                        font-size: 14px;
                        line-height: 1.6;

                        .material-icons {
                            font-size: 18px;
                            margin-top: 2px;

                            &.when-icon {
                                color: #52c41a;
                            }

                            &.condition-icon {
                                color: #1890ff;
                            }

                            &.action-icon {
                                color: #ff9800;
                            }
                        }

                        strong {
                            min-width: 100px;
                            color: #666;
                        }

                        span {
                            flex: 1;
                            color: #1a1a1a;

                            :deep(.field-name) {
                                color: #1890ff;
                                font-weight: 500;
                            }

                            :deep(.value-highlight) {
                                background: #fff7e6;
                                padding: 2px 6px;
                                border-radius: 3px;
                                color: #d46b08;
                                font-weight: 500;
                            }

                            :deep(.group-op) {
                                color: #9254de;
                                font-weight: 700;
                                padding: 0 4px;
                            }

                            :deep(.no-condition),
                            :deep(.no-action) {
                                color: #999;
                                font-style: italic;
                            }

                            :deep(.incomplete) {
                                color: #ff4d4f;
                                font-style: italic;
                            }

                            :deep(.action-assignee),
                            :deep(.action-owner) {
                                color: #1890ff;

                                strong {
                                    color: #096dd9;
                                }
                            }

                            :deep(.action-tag),
                            :deep(.action-email) {
                                color: #ff9800;

                                strong {
                                    color: #d46b08;
                                }
                            }

                            :deep(.action-follower),
                            :deep(.action-collaborator),
                            :deep(.action-mark) {
                                color: #52c41a;

                                strong {
                                    color: #389e0d;
                                }
                            }

                            :deep(.action-stage),
                            :deep(.action-priority) {
                                color: #722ed1;

                                strong {
                                    color: #531dab;
                                }
                            }

                            :deep(.action-remove) {
                                color: #ff4d4f;

                                strong {
                                    color: #cf1322;
                                }
                            }

                            :deep(.action-date) {
                                color: #1890ff;

                                strong {
                                    color: #096dd9;
                                }
                            }

                            :deep(.action-checklist),
                            :deep(.action-comment) {
                                color: #13c2c2;

                                strong {
                                    color: #08979c;
                                }

                                em {
                                    color: #666;
                                    font-size: 12px;
                                }
                            }

                            :deep(.action-notification) {
                                color: #52c41a;

                                strong {
                                    color: #389e0d;
                                }

                                em {
                                    color: #666;
                                    font-size: 12px;
                                }
                            }

                            :deep(.action-webhook) {
                                color: #722ed1;

                                strong {
                                    color: #531dab;
                                }

                                em {
                                    color: #666;
                                    font-size: 12px;
                                }
                            }
                        }
                    }
                }
            }
        }

        .else-card {
            background: #fafafa;
            border: 2px dashed #d9d9d9;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 16px;

            .else-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 16px;

                .else-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 16px;
                    font-weight: 600;
                    color: #666;

                    .material-icons {
                        font-size: 20px;
                    }
                }

                .icon-button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 6px;
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    transition: all 0.2s ease;

                    .material-icons {
                        font-size: 18px;
                        line-height: 1;
                    }

                    &:hover {
                        background-color: rgba(0, 0, 0, 0.04);
                    }
                }
            }

            .else-body {
                .no-actions {
                    color: #999;
                    font-style: italic;
                    padding: 12px;
                    text-align: center;
                }
            }
        }

        .add-rule-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 20px;
            background: #ffffff;
            border: 2px dashed #1890ff;
            border-radius: 12px;
            cursor: pointer;
            color: #1890ff;
            font-weight: 600;
            transition: all 0.3s ease;

            &:hover {
                background: #e6f7ff;
                border-color: #40a9ff;
            }

            .material-icons {
                font-size: 24px;
            }
        }
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 400px;
        color: #999;

        .empty-icon {
            .material-icons {
                font-size: 80px;
                color: #d9d9d9;
            }
        }

        h3 {
            font-size: 20px;
            margin: 16px 0 8px 0;
            color: #666;
        }

        p {
            font-size: 14px;
            margin: 0;
        }
    }
}
</style>
